import { GridItemChangeEvent } from '@progress/kendo-react-grid';
import { set } from 'lodash';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

interface UseKendoGridInlineEditingProps {
    idField: string;
    editField: string;
    source: any[];
    data: any[];
    setData: (data: any[]) => void;
    setIsDirty?: React.Dispatch<React.SetStateAction<boolean>>;
}
let datalocal: any = undefined;
const useKendoGridInlineEditing = ({
    idField,
    editField,
    source,
    data,
    setData,
    setIsDirty,
}: UseKendoGridInlineEditingProps) => {
    const enterEdit = (dataItem: any) => {
        const existingNewItemIndex = data.findIndex((item) => item.inEdit === true);
        if (existingNewItemIndex > -1) return;
        const newData = data.map((item) =>
            item[idField] === dataItem[idField] ? { ...item, [editField]: true } : item,
        );
        if (datalocal?.length == 0 || datalocal == undefined) {
            datalocal = data;
        }
        setData(newData);
    };

    // can call backend here
    const update = (dataItem: any) => {
        dataItem[editField] = false;
        delete dataItem.isModified;
        const newData = updateItem(data, dataItem, idField);
        setData(newData);
    };

    const cancel = (dataItem: any) => {
        let originalItem: any;

        if (dataItem?.serverSideInLineEdit) {
            originalItem = datalocal.find((p: any) => p[idField] === dataItem[idField]);
        } else {
            originalItem = source.find((p) => p[idField] === dataItem[idField]);
        }
        let newData;
        if (dataItem?.serverSideInLineEdit) {
            newData = datalocal.map((item: any) =>
                item[idField] === (originalItem ? originalItem[idField] : undefined)
                    ? { ...originalItem, [editField]: false }
                    : item,
            );
        } else {
            newData = data.map((item) => (item[idField] === originalItem[idField] ? originalItem : item));
        }

        setData(newData);
        handleSeIsDirty(false);
    };

    // can call backend here
    const remove = (dataItem: any) => {
        const newData = deleteItem(data, dataItem, idField);
        setData(newData);
    };

    const addNew = () => {
        const existingNewItemIndex = data.findIndex((item) => item.id === -1);
        if (existingNewItemIndex > -1) return;
        const existingNewItemIndexUpdate = data.findIndex((item) => item.inEdit === true);
        if (existingNewItemIndexUpdate > -1) return;

        const newDataItem = {
            [idField]: -1,
            [editField]: true,
        };
        setData([newDataItem, ...data]);
    };

    // can call backend here
    const add = (dataItem: any) => {
        const dataWithoutNewItem = data.filter((item) => item.id !== -1);

        // If the id is still -1 generate a random id to not confuse the grid in deciding what is the new item
        if (dataItem[idField] === -1) {
            dataItem[idField] = uuidv4();
        }

        dataItem[editField] = false;
        delete dataItem.isModified;
        const newData = insertItem(dataWithoutNewItem, dataItem);
        setData(newData);
    };

    const discard = (dataItem: any) => {
        const newData = [...data];
        newData.splice(0, 1);
        setData(newData);
        handleSeIsDirty(false);
    };

    const handleSeIsDirty = (value: boolean) => {
        if (setIsDirty !== undefined) {
            setIsDirty(value);
        }
    };

    const handleItemChange = (event: GridItemChangeEvent) => {
        const { dataItem, field, value } = event;
        set(dataItem, field as any, value);
        set(dataItem, 'isModified' as any, true);
        const newData = data.map((item) => (item[idField] === dataItem[idField] ? dataItem : item));
        setData(newData);
        handleSeIsDirty(true);
    };

    return { addNew, add, discard, enterEdit, update, cancel, remove, handleItemChange };
};

export default useKendoGridInlineEditing;

const insertItem = (data: any[], item: any) => [item, ...data];
const updateItem = (data: any[], item: any, idField: string) =>
    data.map((dataItem) => (dataItem[idField] === item[idField] ? item : dataItem));
const deleteItem = (data: any[], item: any, idField: string) =>
    data.filter((dataItem) => dataItem[idField] !== item[idField]);
